import type {
  TalentNodeDefinition,
  TalentNodeId,
  TalentTreeDefinition,
} from '../types/talent.types';

/**
 * Talent tree.
 *
 * 3 zones: offensive, defensive, utility (elemental dropped — elemental
 * defense now lives in the Pokémon-style emotion-type chart, cf.
 * docs/game-design/stats.md). Each zone has 1 root + 24 small nodes + N
 * notables.
 *
 * No `position`: the renderer (`TalentTreeScreen`) derives node placement
 * purely from the `connectsTo` graph (BFS layers from the category root). A
 * node with an empty `connectsTo` is not displayed.
 *
 * Data-driven pattern: notables carry `effects: [{ handler: 'applyBuff',
 * params: { buffId: '<notable>-buff' } }]`. The companion buff (BUFF_REGISTRY)
 * holds the modifiers / hooks / onApply that implement the real effect.
 * Exceptions: Œil-de-Sage, Pas-de-Loup, Frugal, Pillard — no `effects` (the
 * spent node alone feeds the `TalentEffects.has<X>()` helpers used by other
 * services).
 *
 * Id naming convention:
 * - `talent-<zone>-<n>` for the standard +stats nodes.
 * - `talent-notable-<theme>` for notables.
 *
 * Adjacency is expected to be symmetric (cf. TalentNodeDefinition.connectsTo).
 */

function n(
  id: TalentNodeId,
  partial: Omit<TalentNodeDefinition, 'id'>,
): [TalentNodeId, TalentNodeDefinition] {
  return [id, { id, ...partial }];
}

const nodes: Record<TalentNodeId, TalentNodeDefinition> = Object.fromEntries([
  // ===========================================================================
  // OFFENSIVE zone (top-left: x 100-600, y 40-340)
  // ===========================================================================
  n('talent-offensive-root', {
    label: 'Frappe brute',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-1',
      'talent-offensive-3',
      'talent-offensive-6',
      'talent-offensive-8',
      'talent-offensive-9',
      'talent-offensive-17',
      'talent-offensive-22',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 2 }],
    description: '+2 attaque physique',
  }),
  n('talent-offensive-1', {
    label: 'Tranchant accru',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-root',
      'talent-offensive-2',
      'talent-offensive-12',
      'talent-offensive-14',
      'talent-offensive-17',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 3 }],
    description: '+3 attaque physique',
  }),
  n('talent-offensive-2', {
    label: 'Critique exact',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-1',
      'talent-notable-frenzy',
      'talent-offensive-13',
      'talent-offensive-14',
      'talent-offensive-16',
      'talent-offensive-18',
      'talent-offensive-23',
    ],
    modifiers: [{ stat: 'critChancePhysical', amount: 5 }],
    description: '+5% chance critique physique',
  }),
  n('talent-offensive-3', {
    label: 'Magie raffinée',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-root',
      'talent-offensive-4',
      'talent-offensive-10',
      'talent-offensive-15',
      'talent-offensive-22',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 3 }],
    description: '+3 attaque magique',
  }),
  n('talent-offensive-4', {
    label: 'Œil aiguisé magique',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-3',
      'talent-notable-frenzy',
      'talent-offensive-11',
      'talent-offensive-15',
      'talent-offensive-16',
      'talent-offensive-19',
    ],
    modifiers: [{ stat: 'critChanceMagic', amount: 5 }],
    description: '+5% chance critique magique',
  }),
  n('talent-offensive-5', {
    label: 'Lame ébréchée',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-6',
      'talent-offensive-8',
      'talent-offensive-17',
      'talent-offensive-23',
      'talent-notable-living-spear',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 1 }],
    description: '+1 attaque physique',
  }),
  n('talent-offensive-6', {
    label: 'Murmure arcane',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-5',
      'talent-offensive-7',
      'talent-offensive-root',
      'talent-notable-living-spear',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 1 }],
    description: '+1 attaque magique',
  }),
  n('talent-offensive-7', {
    label: 'Coup ferme',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-6',
      'talent-offensive-9',
      'talent-offensive-22',
      'talent-notable-living-spear',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 2 }],
    description: '+2 attaque physique',
  }),
  n('talent-offensive-8', {
    label: 'Estoc rapide',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-5',
      'talent-offensive-root',
      'talent-offensive-12',
      'talent-offensive-17',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 2 }],
    description: '+2 attaque physique',
  }),
  n('talent-offensive-9', {
    label: 'Fil incantatoire',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-7',
      'talent-offensive-root',
      'talent-offensive-10',
      'talent-offensive-22',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 2 }],
    description: '+2 attaque magique',
  }),
  n('talent-offensive-10', {
    label: 'Verbe puissant',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-9',
      'talent-offensive-24',
      'talent-offensive-3',
      'talent-notable-war-cry',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 3 }],
    description: '+3 attaque magique',
  }),
  n('talent-offensive-11', {
    label: 'Coup massif',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-24',
      'talent-offensive-4',
      'talent-offensive-20',
      'talent-notable-war-cry',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 3 }],
    description: '+3 attaque physique',
  }),
  n('talent-offensive-12', {
    label: 'Œil acéré',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-8',
      'talent-offensive-1',
      'talent-offensive-13',
    ],
    modifiers: [{ stat: 'critChancePhysical', amount: 1 }],
    description: '+1% chance critique physique',
  }),
  n('talent-offensive-13', {
    label: 'Œil arcane',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-12',
      'talent-offensive-2',
      'talent-offensive-21',
    ],
    modifiers: [{ stat: 'critChanceMagic', amount: 1 }],
    description: '+1% chance critique magique',
  }),
  n('talent-offensive-14', {
    label: 'Frappe ferme',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-1',
      'talent-offensive-2',
      'talent-offensive-15',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 2 }],
    description: '+2 attaque physique',
  }),
  n('talent-offensive-15', {
    label: 'Verbe ferme',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-3',
      'talent-offensive-4',
      'talent-offensive-14',
      'talent-offensive-24',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 2 }],
    description: '+2 attaque magique',
  }),
  n('talent-offensive-16', {
    label: 'Geste vif',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-2',
      'talent-offensive-4',
      'talent-notable-frenzy',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-offensive-17', {
    label: 'Carrure',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-5',
      'talent-offensive-8',
      'talent-offensive-1',
      'talent-offensive-root',
    ],
    modifiers: [{ stat: 'physicalAttack', amount: 1 }],
    description: '+1 attaque physique',
  }),
  n('talent-offensive-18', {
    label: 'Vue perçante',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-2',
      'talent-offensive-21',
      'talent-notable-frenzy',
      'talent-offensive-23',
    ],
    modifiers: [{ stat: 'critChancePhysical', amount: 2 }],
    description: '+2% chance critique physique',
  }),
  n('talent-offensive-19', {
    label: 'Œil-de-Lynx',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-4',
      'talent-notable-frenzy',
      'talent-offensive-20',
      'talent-notable-bloodletter',
    ],
    modifiers: [{ stat: 'critChanceMagic', amount: 2 }],
    description: '+2% chance critique magique',
  }),
  n('talent-offensive-20', {
    label: 'Coup brutal',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-11',
      'talent-offensive-19',
      'talent-notable-bloodletter',
    ],
    modifiers: [{ stat: 'critMultiplier', percent: 5 }],
    description: '+5% multiplicateur critique',
  }),
  n('talent-offensive-21', {
    label: 'Coup décisif',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-13',
      'talent-offensive-18',
      'talent-notable-precision',
    ],
    modifiers: [{ stat: 'critMultiplier', percent: 10 }],
    description: '+10% multiplicateur critique',
  }),
  n('talent-offensive-22', {
    label: 'Souffle court',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-7',
      'talent-offensive-9',
      'talent-offensive-3',
      'talent-offensive-root',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 1 }],
    description: '+1 attaque magique',
  }),
  n('talent-offensive-23', {
    label: 'Cadence rapide',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-2',
      'talent-offensive-18',
      'talent-offensive-5',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-offensive-24', {
    label: 'Souffle arcanique',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-10',
      'talent-offensive-11',
      'talent-offensive-15',
      'talent-notable-war-cry',
    ],
    modifiers: [{ stat: 'magicAttack', amount: 2 }],
    description: '+2 attaque magique',
  }),
  n('talent-notable-frenzy', {
    label: 'Frénésie',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-2',
      'talent-offensive-4',
      'talent-offensive-16',
      'talent-offensive-18',
      'talent-offensive-19',
    ],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-frenzy-buff',
          name: 'Frénésie',
          damageMultiplierConditions: [
            {
              multiplier: 1.2,
              conditions: [{ check: 'hpBelow', params: { threshold: 30 } }],
            },
          ],
        },
      },
    ],
    description: '+20% dégâts quand HP < 30%',
  }),
  n('talent-notable-precision', {
    label: 'Précision',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: ['talent-offensive-21'],
    isNotable: true,
    modifiers: [
      { stat: 'critMultiplier', percent: 10 },
      { stat: 'critChancePhysical', amount: 5 },
      { stat: 'critChanceMagic', amount: 5 },
    ],
    description: '+10% multiplicateur crit, +5% chance crit',
  }),
  n('talent-notable-bloodletter', {
    label: 'Saigneur',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: ['talent-offensive-19', 'talent-offensive-20'],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-bloodletter-buff',
          name: 'Saigneur',
          hooks: [
            {
              event: 'onKill',
              handler: 'applyBuffInNeighborsCells',
              params: {
                buffId: 'bleed-bloodletter',
                radius: 1,
                targetTeam: 'opposite',
              },
            },
          ],
        },
      },
    ],
    description: 'Les kills infligent saignement aux mobs adjacents',
  }),
  n('talent-notable-living-spear', {
    label: 'Lance-Vivante',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-5',
      'talent-offensive-6',
      'talent-offensive-7',
    ],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-living-spear-buff',
          name: 'Lance-Vivante',
          onApply: [
            {
              handler: 'modifySpellByActionType',
              params: {
                actionType: 'physical',
                minRange: 2,
                step: { handler: 'addSpellRange', params: { amount: 1 } },
              },
            },
          ],
        },
      },
    ],
    description: '+1 portée aux sorts physiques distance',
  }),
  n('talent-notable-war-cry', {
    label: 'Cri-de-Guerre',
    category: 'offensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-offensive-10',
      'talent-offensive-11',
      'talent-offensive-24',
    ],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-war-cry-buff',
          name: 'Cri-de-Guerre',
          hooks: [
            {
              event: 'onFloorEnter',
              handler: 'applyBuff',
              params: { buffId: 'talent-war-cry-burst' },
            },
          ],
        },
      },
    ],
    description: '+20% dégâts pendant 30s en entrant dans un nouvel étage',
  }),

  // ===========================================================================
  // DEFENSIVE zone (top-right: x 600-1100, y 40-340) — mirror of offensive
  // ===========================================================================
  n('talent-defensive-root', {
    label: 'Constitution',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-1',
      'talent-defensive-3',
      'talent-defensive-6',
      'talent-defensive-8',
      'talent-defensive-9',
      'talent-defensive-17',
      'talent-defensive-22',
    ],
    modifiers: [{ stat: 'maxLife', amount: 10 }],
    description: '+10 vie max',
  }),
  n('talent-defensive-1', {
    label: 'Garde physique',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-root',
      'talent-defensive-2',
      'talent-defensive-12',
      'talent-defensive-14',
      'talent-defensive-17',
    ],
    modifiers: [{ stat: 'physicalDefense', amount: 3 }],
    description: '+3 défense physique',
  }),
  n('talent-defensive-2', {
    label: 'Endurance',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-1',
      'talent-notable-endurance',
      'talent-defensive-13',
      'talent-defensive-14',
      'talent-defensive-16',
      'talent-defensive-18',
      'talent-defensive-23',
    ],
    modifiers: [{ stat: 'maxLife', amount: 15 }],
    description: '+15 vie max',
  }),
  n('talent-defensive-3', {
    label: 'Garde magique',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-root',
      'talent-defensive-4',
      'talent-defensive-10',
      'talent-defensive-15',
      'talent-defensive-22',
    ],
    modifiers: [{ stat: 'magicDefense', amount: 3 }],
    description: '+3 défense magique',
  }),
  n('talent-defensive-4', {
    label: 'Bouclier mental',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-3',
      'talent-notable-endurance',
      'talent-defensive-11',
      'talent-defensive-15',
      'talent-defensive-16',
      'talent-defensive-19',
    ],
    modifiers: [{ stat: 'maxShield', amount: 10 }],
    description: '+10 bouclier max',
  }),
  n('talent-defensive-5', {
    label: 'Constitution mineure',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-6',
      'talent-defensive-8',
      'talent-defensive-17',
      'talent-notable-hardy',
    ],
    modifiers: [{ stat: 'maxLife', amount: 5 }],
    description: '+5 vie max',
  }),
  n('talent-defensive-6', {
    label: 'Vigueur',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-5',
      'talent-defensive-7',
      'talent-defensive-root',
      'talent-notable-hardy',
    ],
    modifiers: [{ stat: 'maxLife', amount: 5 }],
    description: '+5 vie max',
  }),
  n('talent-defensive-7', {
    label: 'Os solide',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-6',
      'talent-defensive-9',
      'talent-defensive-22',
      'talent-notable-hardy',
    ],
    modifiers: [{ stat: 'maxLife', amount: 10 }],
    description: '+10 vie max',
  }),
  n('talent-defensive-8', {
    label: 'Cuir épais',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-5',
      'talent-defensive-root',
      'talent-defensive-12',
      'talent-defensive-17',
    ],
    modifiers: [{ stat: 'physicalDefense', amount: 1 }],
    description: '+1 défense physique',
  }),
  n('talent-defensive-9', {
    label: 'Volonté',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-7',
      'talent-defensive-root',
      'talent-defensive-10',
      'talent-defensive-22',
    ],
    modifiers: [{ stat: 'magicDefense', amount: 1 }],
    description: '+1 défense magique',
  }),
  n('talent-defensive-10', {
    label: 'Robustesse',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-9',
      'talent-defensive-24',
      'talent-defensive-3',
      'talent-notable-reflexes',
    ],
    modifiers: [{ stat: 'maxLife', amount: 10 }],
    description: '+10 vie max',
  }),
  n('talent-defensive-11', {
    label: 'Carrure massive',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-24',
      'talent-defensive-4',
      'talent-defensive-20',
      'talent-notable-reflexes',
    ],
    modifiers: [{ stat: 'maxLife', amount: 10 }],
    description: '+10 vie max',
  }),
  n('talent-defensive-12', {
    label: 'Aura prismatique',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-8',
      'talent-defensive-1',
      'talent-defensive-13',
    ],
    modifiers: [{ stat: 'maxShield', amount: 5 }],
    description: '+5 bouclier max',
  }),
  n('talent-defensive-13', {
    label: 'Aura tissée',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-12',
      'talent-defensive-2',
      'talent-defensive-21',
      'talent-defensive-23',
    ],
    modifiers: [{ stat: 'maxShield', amount: 5 }],
    description: '+5 bouclier max',
  }),
  n('talent-defensive-14', {
    label: 'Armure renforcée',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-1',
      'talent-defensive-2',
      'talent-defensive-15',
    ],
    modifiers: [{ stat: 'physicalDefense', amount: 2 }],
    description: '+2 défense physique',
  }),
  n('talent-defensive-15', {
    label: 'Volonté ferme',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-3',
      'talent-defensive-4',
      'talent-defensive-14',
      'talent-defensive-24',
    ],
    modifiers: [{ stat: 'magicDefense', amount: 2 }],
    description: '+2 défense magique',
  }),
  n('talent-defensive-16', {
    label: 'Égide mineure',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-2',
      'talent-defensive-4',
      'talent-notable-endurance',
    ],
    modifiers: [
      { stat: 'maxLife', amount: 5 },
      { stat: 'maxShield', amount: 1 },
    ],
    description: '+5 vie max, +1 bouclier max',
  }),
  n('talent-defensive-17', {
    label: 'Cuir tanné',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-5',
      'talent-defensive-8',
      'talent-defensive-1',
      'talent-defensive-root',
    ],
    modifiers: [{ stat: 'maxLife', amount: 5 }],
    description: '+5 vie max',
  }),
  n('talent-defensive-18', {
    label: 'Souffle régénérant',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-2',
      'talent-defensive-21',
      'talent-notable-endurance',
      'talent-defensive-23',
    ],
    modifiers: [{ stat: 'maxShield', amount: 10 }],
    description: '+10 bouclier max',
  }),
  n('talent-defensive-19', {
    label: 'Égide tenace',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-4',
      'talent-notable-endurance',
      'talent-defensive-20',
      'talent-notable-open-wounds',
    ],
    modifiers: [{ stat: 'maxShield', amount: 10 }],
    description: '+10 bouclier max',
  }),
  n('talent-defensive-20', {
    label: 'Régénération onirique',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-11',
      'talent-defensive-19',
      'talent-notable-open-wounds',
    ],
    modifiers: [{ stat: 'shieldRegen', amount: 0.5 }],
    description: '+0.5 régen bouclier/s',
  }),
  n('talent-defensive-21', {
    label: 'Pacte ininterrompu',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-13',
      'talent-defensive-18',
      'talent-notable-spirit-shield',
    ],
    modifiers: [{ stat: 'shieldRegen', amount: 0.5 }],
    description: '+0.5 régen bouclier/s',
  }),
  n('talent-defensive-22', {
    label: 'Force tranquille',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-7',
      'talent-defensive-9',
      'talent-defensive-3',
      'talent-defensive-root',
    ],
    modifiers: [
      { stat: 'maxLife', amount: 5 },
      { stat: 'physicalDefense', amount: 1 },
    ],
    description: '+5 vie max, +1 défense physique',
  }),
  n('talent-defensive-23', {
    label: 'Calme intérieur',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-2',
      'talent-defensive-18',
      'talent-defensive-13',
    ],
    modifiers: [
      { stat: 'maxLife', amount: 5 },
      { stat: 'maxShield', amount: 1 },
    ],
    description: '+5 vie max, +1 bouclier max',
  }),
  n('talent-defensive-24', {
    label: 'Conviction',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-10',
      'talent-defensive-11',
      'talent-defensive-15',
      'talent-notable-reflexes',
    ],
    modifiers: [
      { stat: 'maxLife', amount: 5 },
      { stat: 'magicDefense', amount: 1 },
    ],
    description: '+5 vie max, +1 défense magique',
  }),
  n('talent-notable-endurance', {
    label: 'Endurance',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-2',
      'talent-defensive-4',
      'talent-defensive-16',
      'talent-defensive-18',
      'talent-defensive-19',
    ],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-endurance-buff',
          name: 'Endurance',
          damageTakenMultiplierConditions: [
            {
              multiplier: 0.9,
              conditions: [{ check: 'hpAbove', params: { threshold: 70 } }],
            },
          ],
        },
      },
    ],
    description: '-10% dégâts reçus quand HP > 70%',
  }),
  n('talent-notable-hardy', {
    label: 'Vivace',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-5',
      'talent-defensive-6',
      'talent-defensive-7',
    ],
    isNotable: true,
    modifiers: [{ stat: 'lifeRegenPerSecond', amount: 0.5 }],
    description: '+0.5 régen vie/s hors combat',
  }),
  n('talent-notable-spirit-shield', {
    label: "Bouclier-d'Esprit",
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: ['talent-defensive-21'],
    isNotable: true,
    modifiers: [
      { stat: 'maxShield', amount: 10 },
      { stat: 'shieldRegen', amount: 1 },
    ],
    description: '+10 bouclier max, +1 régen bouclier/s',
  }),
  n('talent-notable-open-wounds', {
    label: 'Plaies-Ouvertes',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: ['talent-defensive-19', 'talent-defensive-20'],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-open-wounds-buff',
          name: 'Plaies-Ouvertes',
          damageTakenMultiplierConditions: [
            {
              multiplier: 0.8,
              conditions: [{ check: 'hpBelow', params: { threshold: 30 } }],
            },
          ],
        },
      },
    ],
    description: '-20% dégâts reçus quand HP < 30%',
  }),
  n('talent-notable-reflexes', {
    label: 'Réflexes',
    category: 'defensive',
    iconCellIndex: 0,
    connectsTo: [
      'talent-defensive-10',
      'talent-defensive-11',
      'talent-defensive-24',
    ],
    isNotable: true,
    effects: [
      {
        handler: 'grantPassive',
        params: {
          id: 'talent-reflexes-buff',
          name: 'Réflexes',
          dodgeChance: 0.1,
          dodgeMeleeOnly: true,
        },
      },
    ],
    description: "10% chance d'esquiver une attaque mêlée",
  }),

  // ===========================================================================
  // UTILITY zone (bottom-left: x 100-600, y 440-740) — Y-mirror of offensive
  // ===========================================================================
  n('talent-utility-root', {
    label: 'Vigilance',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-1',
      'talent-utility-3',
      'talent-utility-6',
      'talent-utility-8',
      'talent-utility-9',
      'talent-utility-17',
      'talent-utility-22',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-1', {
    label: 'Lecture rapide',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-root',
      'talent-utility-2',
      'talent-utility-12',
      'talent-utility-14',
      'talent-utility-17',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-2', {
    label: 'Foulée légère',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-1',
      'talent-notable-traveler',
      'talent-utility-13',
      'talent-utility-14',
      'talent-utility-16',
      'talent-utility-18',
      'talent-utility-23',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 10 }],
    description: '+10% vitesse de déplacement',
  }),
  n('talent-utility-3', {
    label: 'Main longue',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-root',
      'talent-utility-4',
      'talent-utility-10',
      'talent-utility-15',
      'talent-utility-22',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-4', {
    label: 'Geste prompt',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-3',
      'talent-notable-traveler',
      'talent-utility-11',
      'talent-utility-15',
      'talent-utility-16',
      'talent-utility-19',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -5 }],
    description: '-5% temps de cast',
  }),
  n('talent-utility-5', {
    label: 'Pied agile',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-6',
      'talent-utility-8',
      'talent-utility-17',
      'talent-notable-sage-eye',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 5 }],
    description: '+5% vitesse de déplacement',
  }),
  n('talent-utility-6', {
    label: 'Souffle régulier',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-5',
      'talent-utility-7',
      'talent-utility-root',
      'talent-notable-sage-eye',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 5 }],
    description: '+5% vitesse de déplacement',
  }),
  n('talent-utility-7', {
    label: 'Pas leste',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-6',
      'talent-utility-9',
      'talent-utility-22',
      'talent-notable-sage-eye',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 5 }],
    description: '+5% vitesse de déplacement',
  }),
  n('talent-utility-8', {
    label: 'Œil attentif',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-5',
      'talent-utility-root',
      'talent-utility-12',
      'talent-utility-17',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-9', {
    label: 'Doigts agiles',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-7',
      'talent-utility-root',
      'talent-utility-10',
      'talent-utility-22',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-10', {
    label: 'Glanage',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-9',
      'talent-utility-24',
      'talent-utility-3',
      'talent-notable-frugal',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-11', {
    label: 'Geste sûr',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-24',
      'talent-utility-4',
      'talent-utility-20',
      'talent-notable-frugal',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-utility-12', {
    label: 'Vue claire',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-8', 'talent-utility-1', 'talent-utility-13'],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-13', {
    label: 'Vue lointaine',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-12',
      'talent-utility-2',
      'talent-utility-21',
      'talent-utility-23',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-14', {
    label: 'Cadence souple',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-1', 'talent-utility-2', 'talent-utility-15'],
    modifiers: [{ stat: 'moveSpeed', percent: 5 }],
    description: '+5% vitesse de déplacement',
  }),
  n('talent-utility-15', {
    label: 'Main souple',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-3',
      'talent-utility-4',
      'talent-utility-14',
      'talent-utility-24',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-16', {
    label: "Vif d'esprit",
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-2',
      'talent-utility-4',
      'talent-notable-traveler',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-17', {
    label: 'Marche souple',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-5',
      'talent-utility-8',
      'talent-utility-1',
      'talent-utility-root',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-18', {
    label: 'Course fluide',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-2',
      'talent-utility-21',
      'talent-notable-traveler',
      'talent-utility-23',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 10 }],
    description: '+10% vitesse de déplacement',
  }),
  n('talent-utility-19', {
    label: 'Course folle',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-4',
      'talent-notable-traveler',
      'talent-utility-20',
    ],
    modifiers: [{ stat: 'moveSpeed', percent: 10 }],
    description: '+10% vitesse de déplacement',
  }),
  n('talent-utility-20', {
    label: 'Geste large',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-11',
      'talent-utility-19',
      'talent-notable-plunderer',
    ],
    modifiers: [{ stat: 'pickupReach', amount: 1 }],
    description: '+1 portée de ramassage',
  }),
  n('talent-utility-21', {
    label: 'Œil perçant',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-13',
      'talent-utility-18',
      'talent-notable-wolf-step',
    ],
    modifiers: [{ stat: 'viewRange', amount: 1 }],
    description: '+1 portée de vue',
  }),
  n('talent-utility-22', {
    label: 'Geste fluide',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-7',
      'talent-utility-9',
      'talent-utility-3',
      'talent-utility-root',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-utility-23', {
    label: 'Réflexe affûté',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-2', 'talent-utility-18', 'talent-utility-13'],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-utility-24', {
    label: 'Cadence vive',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-10',
      'talent-utility-11',
      'talent-utility-15',
      'talent-notable-frugal',
    ],
    modifiers: [{ stat: 'spellCastTime', percent: -3 }],
    description: '-3% temps de cast',
  }),
  n('talent-notable-traveler', {
    label: 'Voyageur',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: [
      'talent-utility-2',
      'talent-utility-4',
      'talent-utility-16',
      'talent-utility-18',
      'talent-utility-19',
    ],
    isNotable: true,
    modifiers: [{ stat: 'inventorySlots', amount: 1 }],
    description: "+1 slot d'inventaire",
  }),
  // Œil-de-Sage / Pas-de-Loup / Frugal / Pillard: no `effects` nor associated
  // buff — the spent node alone feeds the `TalentEffects.has<X>()` helpers that
  // drive the consuming services (LocationService, ShopService, LootService).
  n('talent-notable-sage-eye', {
    label: 'Œil-de-Sage',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-5', 'talent-utility-6', 'talent-utility-7'],
    isNotable: true,
    description: 'Voit le contenu des coffres avant ouverture',
  }),
  n('talent-notable-wolf-step', {
    label: 'Pas-de-Loup',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-21'],
    isNotable: true,
    description: '-50% chance de déclencher les pièges',
  }),
  n('talent-notable-frugal', {
    label: 'Frugal',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-10', 'talent-utility-11', 'talent-utility-24'],
    isNotable: true,
    description: '-15% prix chez les marchands',
  }),
  n('talent-notable-plunderer', {
    label: 'Pillard',
    category: 'utility',
    iconCellIndex: 0,
    connectsTo: ['talent-utility-20'],
    isNotable: true,
    description: '+20% rolls de drops',
  }),
]);

export const TALENT_TREE: TalentTreeDefinition = {
  nodes,
  roots: [
    'talent-offensive-root',
    'talent-defensive-root',
    'talent-utility-root',
  ],
  backgroundAsset: '/sprites/ui/talent-tree/cork.jpg',
  iconAtlasAsset: 'placeholder/talent-tree-icons.png',
  viewport: { width: 1200, height: 800 },
};
